import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "../utils/apiHandler";
import { useTranslation } from "react-i18next";

const Camera = () => {
  const navigate = useNavigate();
  const [svgData, setSvgData] = useState("");
  const [scannedText, setScannedText] = useState(null);
  const [permissionError, setPermissionError] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState("");
  const qrRef = useRef(null);
  const scannerRef = useRef(null);
  const { t } = useTranslation();

  const rawHandle = localStorage.getItem("handle");
  const handle = rawHandle ? JSON.parse(rawHandle) : null;

  // Fetch QR code from backend
  useEffect(() => {
    const getQRCode = async () => {
      try {
        const res = await api.get(`/users/${handle.user.id}/qrcode`, {
          headers: {
            Accept: "image/svg+xml",
          },
          responseType: "text",
        });
        setSvgData(res.data);
      } catch (error) {
        console.log(error, "Error in getting QR code");
      }
    };

    if (handle?.user?.id) {
      getQRCode();
    }
  }, [handle]);

  // Start scanning using html5-qrcode
  useEffect(() => {
    const scannerId = "qr-scanner";

    const handlePermissionError = error => {
      console.warn("Camera permission error:", error);

      if (
        error.message?.includes("NotAllowedError") ||
        error.name === "NotAllowedError"
      ) {
        setPermissionError(true);
        setPermissionMessage(
          "Camera access denied. Please enable camera permissions in your browser settings and refresh the page.",
        );
      } else if (
        error.message?.includes("NotFoundError") ||
        error.name === "NotFoundError"
      ) {
        setPermissionError(true);
        setPermissionMessage("No camera found on this device.");
      } else if (
        error.message?.includes("NotReadableError") ||
        error.name === "NotReadableError"
      ) {
        setPermissionError(true);
        setPermissionMessage(
          "Camera is already in use by another application.",
        );
      } else {
        setPermissionError(true);
        setPermissionMessage(
          "Unable to access camera. Please check your device settings.",
        );
      }

      return true;
    };

    const startScanner = async () => {
      try {
        const html5QrCode = new Html5Qrcode(scannerId);
        scannerRef.current = html5QrCode;

        const cameras = await Html5Qrcode.getCameras();

        if (cameras && cameras.length > 0) {
          const backCamera = cameras.find(
            camera =>
              camera.label.toLowerCase().includes("back") ||
              camera.label.toLowerCase().includes("environment"),
          );

          const selectedCameraId = backCamera ? backCamera.id : cameras[0].id;

          await html5QrCode.start(
            selectedCameraId,
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
            },
            decodedText => {
              setScannedText(decodedText); // Show the full scanned link
            },
            () => {
              // Handle QR scan errors silently (these are normal when no QR code is detected)
              // Don't log or throw these errors as they're expected behavior
            },
          );
        } else {
          setPermissionError(true);
          setPermissionMessage("No cameras found on this device.");
        }
      } catch (error) {
        // Handle permission and camera errors without sending to Sentry
        handlePermissionError(error);
      }
    };

    startScanner();

    return () => {
      if (scannerRef.current) {
        scannerRef.current
          .stop()
          .then(() => {
            if (scannerRef.current) {
              scannerRef.current.clear();
            }
          })
          .catch(err => {
            // Silently handle cleanup errors - these are common and not critical
            console.warn("Scanner cleanup warning:", err.message);
          });
      }
    };
  }, []);

  return (
    <div className="h-dvh w-dvw flex flex-col relative bg-black">
      {/* Back Button */}
      <div
        className="p-3 z-10 absolute top-0 left-0"
        onClick={() => navigate(-1)}
      >
        <img src="/icons/backbtn-white.svg" alt="Back" />
      </div>

      {/* QR Scanner Full Screen */}
      <div className="flex-1 flex flex-col mb-2 items-center justify-center relative">
        {permissionError ? (
          /* Permission Error Message */
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 z-30">
            <div className="bg-white rounded-lg p-6 mx-4 max-w-sm text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Camera Access Required
              </h3>
              <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                {permissionMessage}
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t("retry")}
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {t("back")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            id="qr-scanner"
            ref={qrRef}
            className="w-full h-full absolute top-0 left-0"
          />
        )}

        {/* Scanned Link Overlay (Clickable) */}
        {scannedText && !permissionError && (
          <a
            href={scannedText}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute z-20 bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg text-sm sm:text-base max-w-[90%] break-words text-center hover:bg-opacity-100 transition"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            {scannedText}
          </a>
        )}
      </div>

      {/* Bottom QR SVG */}
      <div className="h-[50vh] bg-bg-grey w-full flex items-center justify-center">
        <div
          className="qrcode-wrapper"
          dangerouslySetInnerHTML={{ __html: svgData }}
        />
      </div>
    </div>
  );
};

export default Camera;
